import { useQuery } from 'convex/react';
import { api } from '../../../../backend/convex/_generated/api';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Icons } from '@/components/admin/Icons';
import { useAuth } from '@/context/use-auth';
import type { AdminAnalytics } from '@/types';

export function AdminDashboard() {
  const { user } = useAuth();
  const convexAnalytics = useQuery(
    api.admin.getAnalytics,
    user ? { clerkId: user.id } : "skip"
  ) as AdminAnalytics | undefined;

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <header className="mb-8">
          <h1 className="text-2xl font-semibold text-stone-800 mb-2">Dashboard</h1>
          <p className="text-stone-500">Overview of your platform</p>
        </header>

        {!convexAnalytics ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white p-6 rounded-lg border border-stone-200 animate-pulse">
                <div className="h-4 w-24 bg-stone-100 rounded mb-4"></div>
                <div className="h-8 w-16 bg-stone-100 rounded"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <div className="flex items-center gap-2 mb-2 text-stone-400">
                  <Icons.Books />
                  <p className="text-sm font-medium">Total Books</p>
                </div>
                <p className="text-3xl font-semibold text-stone-800">{formatNumber(convexAnalytics.overview.totalBooks)}</p>
                <p className="text-xs text-teal-600 mt-1">{convexAnalytics.overview.publishedBooks} published</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <div className="flex items-center gap-2 mb-2 text-stone-400">
                  <Icons.Users />
                  <p className="text-sm font-medium">Total Users</p>
                </div>
                <p className="text-3xl font-semibold text-stone-800">{formatNumber(convexAnalytics.overview.totalUsers)}</p>
                <p className="text-xs text-stone-400 mt-1">Active readers</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <div className="flex items-center gap-2 mb-2 text-stone-400">
                  <Icons.Token />
                  <p className="text-sm font-medium">Tokens in Circulation</p>
                </div>
                <p className="text-3xl font-semibold text-stone-800">{formatNumber(convexAnalytics.tokens.inCirculation)}</p>
                <p className="text-xs text-stone-400 mt-1">{formatNumber(convexAnalytics.tokens.purchased)} purchased</p>
              </div>

              <div className="bg-white p-6 rounded-lg border border-stone-200">
                <div className="flex items-center gap-2 mb-2 text-stone-400">
                  <Icons.Activity />
                  <p className="text-sm font-medium">Tokens Spent</p>
                </div>
                <p className="text-3xl font-semibold text-stone-800">{formatNumber(convexAnalytics.tokens.spent)}</p>
                <p className="text-xs text-stone-400 mt-1">On chapter unlocks</p>
              </div>
            </div>

            {/* Recent Activity (Convex) */}
            <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-stone-200">
                <h2 className="text-lg font-semibold text-stone-800">Recent Activity</h2>
              </div>
              
              <div className="divide-y divide-stone-100">
                {convexAnalytics.recentActivity.length === 0 ? (
                  <div className="px-6 py-8 text-center text-stone-400">
                    No recent activity
                  </div>
                ) : (
                  convexAnalytics.recentActivity.map((activity) => (
                    <div key={activity.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`w-2 h-2 rounded-full ${
                          activity.type === 'purchase' ? 'bg-teal-500' :
                          activity.type === 'spend' ? 'bg-amber-500' :
                          activity.type === 'signup_bonus' ? 'bg-stone-400' :
                          'bg-stone-300'
                        }`}></span>
                        <span className="text-sm text-stone-700 capitalize">
                          {activity.type.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className={`text-sm font-medium ${
                          activity.amount > 0 ? 'text-teal-600' : 'text-red-600'
                        }`}>
                          {activity.amount > 0 ? '+' : ''}{activity.amount} tokens
                        </span>
                        <span className="text-xs text-stone-400">
                          {formatDate(activity.createdAt)}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
